/*
 * Offer pop-up (sections/offer-popup.liquid).
 *
 * Assigns the visitor one split-test path per test, by traffic share, and keeps
 * it in localStorage so they stay on that path. The HubSpot form is mounted only
 * when the pop-up will show; this file is deferred ahead of HubSpot's embed
 * script, so the form is on the page when the embed scans for it. When HubSpot
 * reports the form ready, the path's name goes into its hidden field
 * (split_test_assigned), which HubSpot's workflows branch on.
 *
 * Events carry split_test (the test name) and split_test_assigned (the path
 * name):
 *   - offer_popup_view, offer_popup_close and offer_popup_submit go to the
 *     dataLayer, for GTM to forward to GA4 as custom events;
 *   - only offer_popup_submit goes to PostHog, which then also registers both
 *     as super properties so the submitter's later events (purchases) can be
 *     broken down by path;
 *   - only a submit goes to HubSpot, as the custom behavioural event named in
 *     the section settings.
 */
if (!customElements.get('offer-popup')) {
  customElements.define(
    'offer-popup',
    class OfferPopup extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;

        this.dialog = this.querySelector('dialog');
        this.formSlot = this.querySelector('[data-offer-form]');
        this.formTemplate = this.querySelector('template[data-offer-form-template]');
        if (!this.dialog) return;

        try {
          this.paths = JSON.parse(this.querySelector('[data-offer-paths]').textContent);
        } catch (error) {
          this.paths = [];
        }

        this.querySelectorAll('[data-offer-close]').forEach((button) =>
          button.addEventListener('click', () => this.close('button'))
        );
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close('backdrop');
        });
        this.dialog.addEventListener('cancel', () => {
          this.closeMethod = 'escape';
        });
        this.dialog.addEventListener('close', () => this.afterClose());

        if (this.hasAttribute('data-design-mode')) {
          this.initEditor();
        } else {
          this.initStorefront();
        }
      }

      get test() {
        return this.dataset.test;
      }

      get storageKey() {
        return `legato:offer-popup:${this.test}`;
      }

      /* Storefront */

      initStorefront() {
        if (window.location.pathname === '/challenge') return;
        if (document.documentElement.classList.contains('chrome-none')) return;

        const param = new URLSearchParams(window.location.search).get('offer_popup');
        if (param === 'reset') this.writeState({});
        const state = this.readState();

        if (param && param !== 'reset') {
          // QA preview: show it whatever the visitor's state, record nothing about them.
          this.preview = true;
          const forced = this.paths[parseInt(param, 10) - 1];
          this.path = forced ? forced.name : this.pickPath();
        } else {
          if (!this.formTemplate) return;
          if (state.submitted) return;
          if (state.dismissedUntil && Date.now() < state.dismissedUntil) return;

          const kept = this.paths.find((path) => path.name === state.path && path.weight > 0);
          this.path = kept ? kept.name : this.pickPath();
          if (state.path !== this.path) {
            this.writeState({ ...state, path: this.path, assignedAt: Date.now() });
          }
        }

        this.mountForm();
        this.listenToHubSpot();

        const delay = parseInt(this.dataset.delay, 10);
        window.setTimeout(() => this.openWhenClear(), (Number.isNaN(delay) ? 5 : delay) * 1000);
      }

      // Shares are relative, so 45/45 behaves like 50/50; a 0 share is never drawn.
      pickPath() {
        const live = this.paths.filter((path) => path.weight > 0);
        if (!live.length) return '';

        const total = live.reduce((sum, path) => sum + path.weight, 0);
        let roll = Math.random() * total;
        for (const path of live) {
          roll -= path.weight;
          if (roll < 0) return path.name;
        }
        return live[live.length - 1].name;
      }

      mountForm() {
        if (this.formTemplate && this.formSlot) {
          this.formSlot.replaceChildren(this.formTemplate.content.cloneNode(true));
        }
      }

      // Wait out a hidden tab, the HubSpot cookie banner, or another open dialog.
      openWhenClear() {
        if (document.hidden) {
          document.addEventListener('visibilitychange', () => this.openWhenClear(), { once: true });
          return;
        }
        if (this.isBlocked()) {
          window.setTimeout(() => this.openWhenClear(), 1000);
          return;
        }
        this.open();
        this.track('offer_popup_view');
      }

      isBlocked() {
        const banner = document.getElementById('hs-eu-cookie-confirmation');
        if (banner && banner.getClientRects().length && getComputedStyle(banner).visibility !== 'hidden') return true;
        return [...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== this.dialog);
      }

      open() {
        if (this.dialog.open) return;
        this.closeMethod = null;
        this.dialog.showModal();
        document.documentElement.classList.add('offer-popup-open');
      }

      close(method) {
        if (!this.dialog.open) return;
        this.closeMethod = method;
        this.dialog.close();
      }

      afterClose() {
        document.documentElement.classList.remove('offer-popup-open');
        if (this.hasAttribute('data-design-mode') || this.path === undefined) return;

        if (!this.submitted && !this.preview) {
          const days = parseInt(this.dataset.dismissDays, 10) || 14;
          this.writeState({ ...this.readState(), dismissedUntil: Date.now() + days * 86400000 });
        }
        this.track('offer_popup_close', {
          close_method: this.closeMethod || 'escape',
          submitted: Boolean(this.submitted),
        });
      }

      listenToHubSpot() {
        const formFor = (event) => {
          const form = window.HubSpotFormsV4 && window.HubSpotFormsV4.getFormFromEvent(event);
          return form && form.getFormId() === this.dataset.formId ? form : null;
        };

        window.addEventListener('hs-form-event:on-ready', (event) => {
          const form = formFor(event);
          if (form && this.path && this.dataset.hiddenField) form.setFieldValue(this.dataset.hiddenField, this.path);
        });

        window.addEventListener('hs-form-event:on-submission:success', (event) => {
          if (!formFor(event) || this.submitted) return;
          this.submitted = true;
          this.classList.add('is-submitted');
          if (!this.preview) this.writeState({ ...this.readState(), submitted: true, submittedAt: Date.now() });
          this.track('offer_popup_submit');
        });
      }

      /* Tracking */

      track(event, extra = {}) {
        const props = {
          split_test: this.test,
          split_test_assigned: this.path,
          page_type: this.dataset.pageType,
          ...(this.preview ? { offer_preview: true } : {}),
          ...extra,
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...props });

        if (event !== 'offer_popup_submit') return;

        // A QA submission is a real one, so it still reaches PostHog and HubSpot
        // (flagged offer_preview in PostHog); it just doesn't tag the browser.
        this.withPostHog((posthog) => {
          if (!this.preview) posthog.register({ split_test: props.split_test, split_test_assigned: props.split_test_assigned });
          posthog.capture(event, props);
        });

        const hubspotEvent = this.dataset.hubspotSubmitEvent;
        if (hubspotEvent) {
          window._hsq = window._hsq || [];
          window._hsq.push(['trackCustomBehavioralEvent', { name: hubspotEvent, properties: { split_test_assigned: props.split_test_assigned } }]);
        }
      }

      // PostHog is started by GTM, which can land after the pop-up opens.
      withPostHog(callback, attempt = 0) {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          callback(window.posthog);
        } else if (attempt < 20) {
          window.setTimeout(() => this.withPostHog(callback, attempt + 1), 500);
        }
      }

      /* Storage: localStorage can be missing or throw (private mode, blocked site data). */

      readState() {
        try {
          return JSON.parse(window.localStorage.getItem(this.storageKey)) || {};
        } catch (error) {
          return {};
        }
      }

      writeState(state) {
        try {
          window.localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
          /* The pop-up still works; it just can't remember the visitor. */
        }
      }

      /* Theme editor: open while the section or one of its paths is selected. */

      initEditor() {
        this.mountForm();

        const sectionId = this.dataset.sectionId;
        const mine = (event) => this.isConnected && event.detail && event.detail.sectionId === sectionId;

        document.addEventListener('shopify:section:select', (event) => mine(event) && this.open());
        document.addEventListener('shopify:section:load', (event) => mine(event) && this.open());
        document.addEventListener('shopify:section:deselect', (event) => mine(event) && this.close('editor'));
        document.addEventListener('shopify:block:select', (event) => mine(event) && this.open());
      }
    }
  );
}
